import { createContext, useContext, useMemo } from "react";
import type { ReactNode } from "react";
import { ApiClient, appConfig, type AuthHandle } from "@csa/web-foundation";

// The party-realm API client. UNLIKE the staff console, there is NO four-eyes approval
// gateway — a party principal never approves anything, so the ApiClient is built
// WITHOUT `approvals` (it supports that; the four-eyes recovery path simply never
// fires for party reads). This is precisely why the customer app does NOT use the
// foundation's ApiProvider, which hard-requires the staff ApprovalProvider
// (`useApprovals()` throws without it). A clean shared party/staff ApiProvider
// abstraction could be proposed to the foundation later — flagged, not forced here.
// The context carries BOTH the client and the AuthHandle: the party realm has no staff
// ApprovalProvider, so its fresh-auth seam (stepUp, for KVKK consent grant/withdraw)
// travels alongside the client here rather than through the foundation's staff providers.
interface PartyApi {
  client: ApiClient;
  auth: AuthHandle;
}
const PartyApiContext = createContext<PartyApi | null>(null);

export function PartyApiProvider({
  auth,
  client,
  children,
}: {
  auth: AuthHandle;
  /** Test seam: inject a prebuilt client. */
  client?: ApiClient;
  children: ReactNode;
}) {
  const value = useMemo(
    () => ({ client: client ?? new ApiClient({ baseUrl: appConfig.apiBaseUrl, auth }), auth }),
    [client, auth],
  );
  return <PartyApiContext.Provider value={value}>{children}</PartyApiContext.Provider>;
}

export function usePartyApi(): ApiClient {
  const ctx = useContext(PartyApiContext);
  if (!ctx) throw new Error("usePartyApi must be used within a PartyApiProvider");
  return ctx.client;
}

/** The party-realm fresh-auth seam. `stepUp()` forces re-auth (auth_time ≤ 300s) and
 *  returns a fresh token (or null if abandoned) — call it BEFORE a KVKK consent grant or
 *  withdraw (ADR 0005 §5). Same AuthHandle the ApiClient consumes. */
export function usePartyAuth(): AuthHandle {
  const ctx = useContext(PartyApiContext);
  if (!ctx) throw new Error("usePartyAuth must be used within a PartyApiProvider");
  return ctx.auth;
}
